import React, { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { IconPlus, IconTrash, IconEdit } from "@tabler/icons-react";
import { Button, TabsContent, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Loader } from "../../components/Loader";
import { Criterion } from "../../services/evals";
import { formatDateTime } from "../utils";
import { CriterionRowEditMode } from "./CriterionRowEditMode";
import { CriterionRowViewMode } from "./CriterionRowViewMode";

interface CriteriaTabProps {
  criteria: Criterion[] | undefined;
  criteriaLoading: boolean;
  currentTenant: string;
  setShowCreateCriterionModal: (show: boolean) => void;
  handleDeleteCriterion: (id: string) => void;
  updateCriterionMutation: {
    mutate: (params: {
      id: string;
      data: { name: string; description: string; tenant: string };
    }) => void;
  };
  expandedCriterionId: string | null;
  setExpandedCriterionId: (id: string | null) => void;
  navigate: NavigateFunction;
}

export const CriteriaTab: React.FC<CriteriaTabProps> = ({
  criteria,
  criteriaLoading,
  currentTenant,
  setShowCreateCriterionModal,
  handleDeleteCriterion,
  updateCriterionMutation,
  expandedCriterionId,
  setExpandedCriterionId,
  navigate,
}) => {
  const [editingCriterionId, setEditingCriterionId] = useState<string | null>(null);
  const [editingCriterionData, setEditingCriterionData] = useState<{
    name: string;
    description: string;
    tenant: string;
  } | null>(null);

  return (
    <TabsContent value="criteria">
      {criteriaLoading ? (
        <Loader label="Loading criteria..." />
      ) : (
        <>
          <Stack direction="row" justify="end" className="al-mb-4">
            <Button onClick={() => setShowCreateCriterionModal(true)} variant="outline" size="sm">
              <IconPlus className="al-w-4 al-h-4 al-mr-2" />
              Create Criterion
            </Button>
          </Stack>

          <div
            key="criteria-tab"
            className="al-w-full al-overflow-hidden al-border al-border-gray-200 al-rounded-md"
          >
            <table className="al-w-full al-divide-y al-divide-gray-200">
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" weight="regular" className="al-text-gray-500">
                      Name
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" weight="regular" className="al-text-gray-500">
                      Description
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" weight="regular" className="al-text-gray-500">
                      Updated
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" weight="regular" className="al-text-gray-500">
                      Updated By
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography size="xs" weight="regular" className="al-text-gray-500">
                      Actions
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                {Array.isArray(criteria) && criteria.length > 0 ? (
                  criteria.map(criterion => {
                    const isExpanded = expandedCriterionId === criterion.id;
                    return (
                      <React.Fragment key={criterion.id}>
                        <tr
                          className={`hover:al-bg-gray-50 al-cursor-pointer ${
                            isExpanded ? "al-bg-blue-50" : ""
                          }`}
                          onClick={e => {
                            if ((e.target as HTMLElement).closest("button")) {
                              return;
                            }
                            const newExpandedId = isExpanded ? null : criterion.id;
                            setExpandedCriterionId(newExpandedId);
                            if (newExpandedId) {
                              navigate(`?tab=criteria&criterion_id=${criterion.id}`, {
                                replace: true,
                              });
                            } else {
                              navigate(`?tab=criteria`, { replace: true });
                            }
                          }}
                        >
                          <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium">
                            <Stack direction="row" align="center" gap="sm">
                              <span className="al-text-gray-400">{isExpanded ? "▼" : "▶"}</span>
                              {criterion.name}
                            </Stack>
                          </td>
                          <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
                            {criterion.description}
                          </td>
                          <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
                            {formatDateTime(criterion.updated_at)}
                          </td>
                          <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-600">
                            {criterion.updated_by || "-"}
                          </td>
                          <td
                            className="al-px-4 al-py-3 al-text-[13px]"
                            onClick={e => e.stopPropagation()}
                          >
                            <Stack direction="row" gap="sm">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  if (editingCriterionId === criterion.id) {
                                    // Toggle off edit mode
                                    setEditingCriterionId(null);
                                    setEditingCriterionData(null);
                                  } else {
                                    // Toggle on edit mode
                                    if (!isExpanded) {
                                      setExpandedCriterionId(criterion.id);
                                    }
                                    setEditingCriterionId(criterion.id);
                                    setEditingCriterionData({
                                      name: criterion.name,
                                      description: criterion.description,
                                      tenant: criterion.tenant,
                                    });
                                  }
                                }}
                                className={
                                  editingCriterionId === criterion.id
                                    ? "al-bg-blue-100 al-text-blue-700 al-border-blue-300 al-shadow-inner al-translate-y-[1px]"
                                    : "al-text-blue-600 hover:al-text-blue-700 hover:al-bg-blue-50 al-border-blue-200"
                                }
                              >
                                <IconEdit className="al-w-4 al-h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCriterion(criterion.id)}
                                className="al-text-red-600 hover:al-text-red-700 hover:al-bg-red-50 al-border-red-200"
                              >
                                <IconTrash className="al-w-4 al-h-4" />
                              </Button>
                            </Stack>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="al-p-0">
                              <div className="al-bg-gray-50 al-border-t al-border-b al-border-gray-200">
                                <div className="al-p-6">
                                  {editingCriterionId === criterion.id ? (
                                    <CriterionRowEditMode
                                      editingCriterionData={editingCriterionData}
                                      currentTenant={currentTenant}
                                      setEditingCriterionData={setEditingCriterionData}
                                      setEditingCriterionId={setEditingCriterionId}
                                      onSave={() => {
                                        if (editingCriterionData) {
                                          updateCriterionMutation.mutate({
                                            id: criterion.id,
                                            data: {
                                              name: editingCriterionData.name,
                                              description: editingCriterionData.description,
                                              tenant: editingCriterionData.tenant,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                  ) : (
                                    <CriterionRowViewMode criterion={criterion} />
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="al-text-center al-py-8 al-text-sm al-text-gray-500">
                      No criteria found. Create one to get started!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </TabsContent>
  );
};
